import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Card, Row, Col, Badge, Spinner, Tab, Tabs, Table } from 'react-bootstrap';
import { FaBusinessTime, FaMoneyBillWave, FaChartLine, FaFileInvoice, FaMapMarkerAlt, FaCalendarAlt, FaCheckCircle, FaExclamationTriangle, FaDownload } from 'react-icons/fa';

const CMSModal = ({ show, onHide, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('collection');
  const [selectedService, setSelectedService] = useState('');
  const [collectionDetails, setCollectionDetails] = useState({
    clientName: '',
    contactPerson: '',
    mobile: '',
    email: '',
    address: '',
    city: '',
    pincode: '',
    collectionAmount: '',
    frequency: 'daily',
    startDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [serviceQuote, setServiceQuote] = useState(null);
  const [collections, setCollections] = useState([]);

  // CMS Service types
  const cmsServices = [
    {
      id: 'retail_collection',
      name: 'Retail Collection',
      description: 'Daily cash collection from retail outlets',
      icon: <FaBusinessTime className="text-primary" />,
      minAmount: 5000,
      charges: 0.5 // 0.5% of collection
    },
    {
      id: 'bank_deposit',
      name: 'Bank Deposit Service',
      description: 'Secure bank deposit with reconciliation',
      icon: <FaMoneyBillWave className="text-success" />,
      minAmount: 10000,
      charges: 0.3
    },
    {
      id: 'cash_management',
      name: 'Complete Cash Management',
      description: 'End-to-end cash management solution',
      icon: <FaChartLine className="text-warning" />,
      minAmount: 25000,
      charges: 0.8
    },
    {
      id: 'invoice_collection',
      name: 'Invoice Collection',
      description: 'B2B invoice and payment collection',
      icon: <FaFileInvoice className="text-info" />,
      minAmount: 15000,
      charges: 1.0
    }
  ];

  // Mock collection data
  const mockCollections = [
    {
      id: 'CMS001',
      clientName: 'ABC Retail Store',
      amount: 25000,
      status: 'Collected',
      date: '2025-01-15',
      location: 'Connaught Place, Delhi'
    },
    {
      id: 'CMS002',
      clientName: 'XYZ Electronics',
      amount: 45000,
      status: 'Pending',
      date: '2025-01-15',
      location: 'Nehru Place, Delhi'
    },
    {
      id: 'CMS003',
      clientName: 'PQR Garments',
      amount: 18000,
      status: 'Deposited',
      date: '2025-01-14',
      location: 'Karol Bagh, Delhi'
    }
  ];

  useEffect(() => {
    if (activeTab === 'tracking') {
      setCollections(mockCollections);
    }
  }, [activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedService('');
    setServiceQuote(null);
    setMessage({ type: '', text: '' });
  };

  const validateCollectionDetails = () => {
    const { clientName, contactPerson, mobile, email, collectionAmount } = collectionDetails;
    
    if (!clientName.trim()) return 'Please enter client name';
    if (!contactPerson.trim()) return 'Please enter contact person name';
    if (!/^[6-9]\d{9}$/.test(mobile)) return 'Please enter a valid mobile number';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email';
    if (!collectionAmount || parseFloat(collectionAmount) < 5000) return 'Minimum collection amount is ₹5,000';
    
    return null;
  };

  const generateQuote = async () => {
    const validationError = validateCollectionDetails();
    if (validationError) {
      setMessage({ type: 'error', text: validationError });
      return;
    }

    if (!selectedService) {
      setMessage({ type: 'error', text: 'Please select a CMS service' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      const service = cmsServices.find(s => s.id === selectedService);
      const amount = parseFloat(collectionDetails.collectionAmount);
      const charges = (amount * service.charges) / 100;
      const securityDeposit = Math.max(10000, amount * 0.05); // 5% or min ₹10,000

      const quote = {
        service: service.name,
        collectionAmount: amount,
        charges: charges,
        securityDeposit: securityDeposit,
        frequency: collectionDetails.frequency,
        monthlyCharges: charges * getFrequencyMultiplier(collectionDetails.frequency),
        features: getCMSFeatures(selectedService),
        validTill: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()
      };

      setServiceQuote(quote);
      setMessage({ 
        type: 'success', 
        text: `Quote generated successfully! Monthly charges: ₹${quote.monthlyCharges.toLocaleString()}` 
      });

    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to generate quote. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const setupCMSService = async () => {
    if (!serviceQuote) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Simulate service setup
      await new Promise(resolve => setTimeout(resolve, 3000));

      const serviceData = {
        serviceId: 'CMS' + Date.now(),
        clientName: collectionDetails.clientName,
        service: serviceQuote.service,
        monthlyCharges: serviceQuote.monthlyCharges,
        securityDeposit: serviceQuote.securityDeposit,
        status: 'Active',
        startDate: collectionDetails.startDate || new Date().toLocaleDateString()
      };

      setMessage({ 
        type: 'success', 
        text: `CMS service activated successfully! Service ID: ${serviceData.serviceId}` 
      });

      if (onSuccess) {
        onSuccess(serviceData);
      }

      // Auto-close after 3 seconds
      setTimeout(() => {
        onHide();
      }, 3000);

    } catch (error) {
      setMessage({ type: 'error', text: 'Service setup failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const getFrequencyMultiplier = (frequency) => {
    switch (frequency) {
      case 'daily': return 30;
      case 'weekly': return 4;
      case 'monthly': return 1;
      default: return 30;
    }
  };

  const getCMSFeatures = (serviceId) => {
    const features = {
      retail_collection: [
        'Daily cash pickup',
        'Real-time tracking',
        'SMS notifications',
        'Monthly reports',
        'Insurance coverage'
      ],
      bank_deposit: [
        'Same-day deposit',
        'Bank reconciliation',
        'Digital receipts',
        'Online tracking',
        'Deposit confirmation'
      ],
      cash_management: [
        'Complete cash cycle management',
        'Inventory management',
        'Advanced analytics',
        'Dedicated relationship manager',
        'Custom reporting'
      ],
      invoice_collection: [
        'Door-to-door collection',
        'Payment follow-up',
        'Customer communication',
        'Payment tracking',
        'Dispute resolution'
      ]
    };
    return features[serviceId] || [];
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Collected': 'success',
      'Pending': 'warning', 
      'Deposited': 'primary',
      'Failed': 'danger'
    };
    return <Badge bg={variants[status]}>{status}</Badge>;
  };

  const handleReset = () => {
    setSelectedService('');
    setServiceQuote(null);
    setMessage({ type: '', text: '' });
    setCollectionDetails({
      clientName: '',
      contactPerson: '',
      mobile: '',
      email: '',
      address: '',
      city: '',
      pincode: '',
      collectionAmount: '',
      frequency: 'daily',
      startDate: ''
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <FaBusinessTime className="me-2" />
          Cash Management System (CMS)
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* CMS Service Tabs */}
        <Tabs activeKey={activeTab} onSelect={handleTabChange} className="mb-4">
          <Tab eventKey="collection" title={
            <span>
              <FaMoneyBillWave className="me-2" />
              Setup Service
            </span>
          }>
            <div className="p-2">
              <h6 className="text-primary mb-3">Cash Management Service Setup</h6>
            </div>
          </Tab>
          <Tab eventKey="tracking" title={
            <span>
              <FaChartLine className="me-2" />
              Track Collections
            </span>
          }>
            <div className="p-2">
              <h6 className="text-primary mb-3">Collection Tracking</h6>
            </div>
          </Tab>
          <Tab eventKey="analytics" title={
            <span>
              <FaFileInvoice className="me-2" />
              Reports
            </span>
          }>
            <div className="p-2">
              <h6 className="text-primary mb-3">CMS Analytics & Reports</h6>
            </div>
          </Tab>
        </Tabs>

        {/* Collection Service Setup */}
        {activeTab === 'collection' && (
          <Row>
            {/* Service Selection */}
            <Col md={6}>
              <div className="mb-4">
                <h6 className="mb-3">Select CMS Service</h6>
                {cmsServices.map((service) => (
                  <Card 
                    key={service.id}
                    className={`mb-3 cursor-pointer ${selectedService === service.id ? 'border-primary' : ''}`}
                    onClick={() => setSelectedService(service.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-center">
                        <div className="me-3">
                          {service.icon}
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="mb-1">{service.name}</h6>
                          <small className="text-muted">{service.description}</small>
                          <div className="mt-1">
                            <Badge bg="info" className="me-2">Min: ₹{service.minAmount.toLocaleString()}</Badge>
                            <Badge bg="warning">Charges: {service.charges}%</Badge>
                          </div>
                        </div>
                        {selectedService === service.id && (
                          <FaCheckCircle className="text-success" />
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </Col>

            {/* Client Details Form */}
            <Col md={6}>
              <Card>
                <Card.Header>
                  <FaBusinessTime className="me-2" />
                  Client Details
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Client Name *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Business/Company name"
                          value={collectionDetails.clientName}
                          onChange={(e) => setCollectionDetails({...collectionDetails, clientName: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Contact Person *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Contact person name"
                          value={collectionDetails.contactPerson}
                          onChange={(e) => setCollectionDetails({...collectionDetails, contactPerson: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mobile Number *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Mobile number"
                          value={collectionDetails.mobile}
                          onChange={(e) => setCollectionDetails({...collectionDetails, mobile: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email *</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="Email address"
                          value={collectionDetails.email}
                          onChange={(e) => setCollectionDetails({...collectionDetails, email: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Business Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Complete business address"
                      value={collectionDetails.address}
                      onChange={(e) => setCollectionDetails({...collectionDetails, address: e.target.value})}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="City"
                          value={collectionDetails.city}
                          onChange={(e) => setCollectionDetails({...collectionDetails, city: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Pincode</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Pincode"
                          value={collectionDetails.pincode}
                          onChange={(e) => setCollectionDetails({...collectionDetails, pincode: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Collection Amount *</Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="Daily amount"
                          value={collectionDetails.collectionAmount}
                          onChange={(e) => setCollectionDetails({...collectionDetails, collectionAmount: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Collection Frequency</Form.Label>
                        <Form.Select
                          value={collectionDetails.frequency}
                          onChange={(e) => setCollectionDetails({...collectionDetails, frequency: e.target.value})}
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={collectionDetails.startDate}
                          onChange={(e) => setCollectionDetails({...collectionDetails, startDate: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Service Quote */}
        {serviceQuote && activeTab === 'collection' && (
          <Card className="mt-4">
            <Card.Header className="bg-success text-white">
              <FaCheckCircle className="me-2" />
              CMS Service Quote
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-2"><strong>Service:</strong> {serviceQuote.service}</div>
                  <div className="mb-2"><strong>Collection Amount:</strong> ₹{serviceQuote.collectionAmount.toLocaleString()}</div>
                  <div className="mb-2"><strong>Frequency:</strong> {collectionDetails.frequency}</div>
                  <div className="mb-2"><strong>Monthly Charges:</strong> ₹{serviceQuote.monthlyCharges.toLocaleString()}</div>
                </Col>
                <Col md={6}>
                  <div className="mb-2"><strong>Security Deposit:</strong> ₹{serviceQuote.securityDeposit.toLocaleString()}</div>
                  <div className="mb-2"><strong>Quote Valid Till:</strong> {serviceQuote.validTill}</div>
                  <div className="mb-2">
                    <strong>Features:</strong>
                    <ul className="mb-0 mt-1">
                      {serviceQuote.features.slice(0, 3).map((feature, index) => (
                        <li key={index} className="small">{feature}</li>
                      ))}
                    </ul>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Collection Tracking */}
        {activeTab === 'tracking' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6>Today's Collections</h6>
              <Badge bg="info">Total: ₹88,000</Badge>
            </div>
            
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Collection ID</th>
                  <th>Client Name</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {collections.map((collection) => (
                  <tr key={collection.id}>
                    <td>{collection.id}</td>
                    <td>{collection.clientName}</td>
                    <td>₹{collection.amount.toLocaleString()}</td>
                    <td>{getStatusBadge(collection.status)}</td>
                    <td>{collection.date}</td>
                    <td>
                      <small>
                        <FaMapMarkerAlt className="me-1" />
                        {collection.location}
                      </small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Analytics/Reports */}
        {activeTab === 'analytics' && (
          <div>
            <Row>
              <Col md={3}>
                <Card className="text-center mb-3">
                  <Card.Body>
                    <h4 className="text-primary">₹12.5L</h4>
                    <small>Monthly Collections</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center mb-3">
                  <Card.Body>
                    <h4 className="text-success">156</h4>
                    <small>Total Collections</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center mb-3">
                  <Card.Body>
                    <h4 className="text-warning">₹8,500</h4>
                    <small>Service Charges</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center mb-3">
                  <Card.Body>
                    <h4 className="text-info">98.5%</h4>
                    <small>Success Rate</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <div className="text-center mt-4">
              <Button variant="outline-primary" className="me-2">
                <FaDownload className="me-2" />
                Download Report
              </Button>
              <Button variant="outline-success">
                <FaCalendarAlt className="me-2" />
                Schedule Report
              </Button>
            </div>
          </div>
        )}

        {/* Message */}
        {message.text && (
          <Alert variant={message.type === 'error' ? 'danger' : 'success'} className="mt-3">
            {message.text}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {selectedService && activeTab === 'collection' && (
              <Button variant="outline-secondary" onClick={handleReset}>
                Reset
              </Button>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide} className="me-2">
              Close
            </Button>
            {activeTab === 'collection' && !serviceQuote && (
              <Button 
                variant="primary" 
                onClick={generateQuote}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FaChartLine className="me-2" />
                    Get Quote
                  </>
                )}
              </Button>
            )}
            {activeTab === 'collection' && serviceQuote && (
              <Button 
                variant="success" 
                onClick={setupCMSService}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Setting up...
                  </>
                ) : (
                  <>
                    <FaBusinessTime className="me-2" />
                    Activate Service
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CMSModal;
